import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Image,
  Skeleton,
  useColorMode,
  VStack,
} from "@chakra-ui/react";
import { RefObject, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { assets } from "../../assets/assets";
import { ChooseEventDepartmentModal } from "../departments/ChooseEventDepartmentModal";
import { useCreateSourcingEvent } from "../departments/useCreateSourcingEvent";
import { CreateContractModal } from "../../pages/contracts/create/CreateContractModal";
import { CreateProjectModal } from "../../pages/projects/CreateProjectModal";
import { urls } from "../../urls";
import { useCreateNeed } from "../../pages/behov/useCreateNeed";
import { AssistantPanelTrigger } from "../agent/AssistantPanelTrigger";
import type { SubAccess } from "../subscription/useSub";
import { navRoleFor, useNavAccess } from "./useNavAccess";
import { NotificationBell } from "../notifications/NotificationBell";
import type { AppRole, NavSection, NavSectionAction, SectionActionKind } from "./navConfig";
import { navConfig } from "./navConfig";
import { isNavItemActive } from "./navActive";
import { SidebarSection } from "./SidebarSection";
import { SidebarUserMenu } from "./SidebarUserMenu";
import { useKeyboardModality } from "../hooks/useKeyboardModality";
import { useCollapsedSections } from "./useCollapsedSections";
import { NAV_REGION_ID } from "./useRegionToggle";
import { requestContentFocus } from "./useFocusFirstRow";
import type { NavBadges } from "./useNavBadges";

const SIDEBAR_WIDTH = "288px";
const ANCHORED_TO_VIEWPORT = { top: "0", right: "0", bottom: "0", left: "0", w: "auto", h: "auto" };

interface Props {
  isOpen: boolean;
  onClose: () => void;
  role: AppRole;
  badges: NavBadges;
}

const SidebarContent = ({
  sections,
  role,
  access,
  badges,
  isCollapsed,
  onToggle,
  onNavigate,
  onAction,
  pendingActionKind,
  regionId,
  navRef,
  resolving,
}: {
  sections: NavSection[];
  role: AppRole;
  access: SubAccess;
  badges: NavBadges;
  isCollapsed: (sectionKey: string) => boolean;
  onToggle: (sectionKey: string) => void;
  onNavigate: (path: string, query?: string) => void;
  onAction: (action: NavSectionAction) => void;
  pendingActionKind: SectionActionKind | null;
  regionId?: string;
  navRef?: RefObject<HTMLDivElement>;
  resolving?: boolean;
}) => {
  const { colorMode } = useColorMode();
  const { t } = useTranslation();
  return (
    <Flex direction="column" h="full">
      <Flex
        align="center"
        justify="space-between"
        h="60px"
        px="4"
        flexShrink={0}
        borderBottom="1px solid"
        borderColor="smBorder"
      >
        <RouterLink to={urls.app.home}>
          <Image src={colorMode === "dark" ? assets.logo_dark : assets.logo} alt="SourceMagnet" h="26px" />
        </RouterLink>
        {/* Desktop only: on mobile the (drawer) sidebar is paired with the top bar, which carries the bell. */}
        <Flex display={{ base: "none", lg: "flex" }} align="center">
          <AssistantPanelTrigger />
          <NotificationBell placement="bottom-start" />
        </Flex>
      </Flex>
      <VStack
        as="nav"
        ref={navRef}
        id={regionId}
        tabIndex={-1}
        aria-label={t("Main navigation") ?? undefined}
        outline="none"
        flex="1"
        align="stretch"
        spacing="0"
        px="3"
        py="2"
        overflowY="auto"
      >
        {resolving ? (
          <VStack align="stretch" spacing="2" pt="2" aria-busy="true" aria-label={t("Loading the menu") ?? undefined}>
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} h="8" borderRadius="md" />
            ))}
          </VStack>
        ) : sections.map((section) => (
          <SidebarSection
            key={section.titleKey}
            section={section}
            role={role}
            access={access}
            badges={badges}
            isCollapsed={isCollapsed(section.titleKey)}
            onToggle={onToggle}
            onNavigate={onNavigate}
            onAction={onAction}
            pendingActionKind={pendingActionKind}
          />
        ))}
      </VStack>
      <SidebarUserMenu />
    </Flex>
  );
};

export const Sidebar = ({ isOpen, onClose, role: appRole, badges }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { create: createNeed } = useCreateNeed();
  const createEvent = useCreateSourcingEvent((eventId) => navigate(urls.app.sourcingEvents.edit.go(eventId).info));
  const { access, owned, resolving } = useNavAccess();
  const role = navRoleFor(appRole, owned);
  const [showCreateContractModal, setShowCreateContractModal] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const usingKeyboard = useKeyboardModality();
  const drawerNavRef = useRef<HTMLDivElement>(null);

  const sections = navConfig.filter((section) => !section.access || access[section.access]);
  const activeSectionKey = sections.find((section) =>
    section.items.some((item) => isNavItemActive(item, location.pathname, location.search)),
  )?.titleKey;
  // Lifted above the two SidebarContent instances (desktop rail and mobile drawer) so folding a
  // section in one is reflected in the other.
  const { isCollapsed, toggle } = useCollapsedSections(
    sections.map((section) => section.titleKey),
    activeSectionKey,
  );

  const onNavigate = (path: string, query?: string) => {
    if (path === urls.app.needs.new) {
      void createNeed();
      onClose();
      return;
    }
    // Only for the keyboard: a pointer user has their attention where they clicked, and moving
    // focus into the list would scroll the page out from under them.
    if (usingKeyboard()) requestContentFocus();
    navigate(query ? `${path}?${query}` : path);
    onClose();
  };

  const actionHandlers: Record<SectionActionKind, () => void> = {
    createSourcingEvent: () => {
      onClose();
      createEvent.start();
    },
    createContract: () => setShowCreateContractModal(true),
    createProject: () => setShowCreateProjectModal(true),
  };

  const onAction = (action: NavSectionAction) => {
    actionHandlers[action.kind]();
    onClose();
  };
  const pendingActionKind: SectionActionKind | null = createEvent.isCreating ? "createSourcingEvent" : null;

  return (
    <>
      <Box
        as="aside"
        display={{ base: "none", lg: "flex" }}
        flexDirection="column"
        w={SIDEBAR_WIDTH}
        flexShrink={0}
        h="full"
        overflow="hidden"
        bg="smBackground"
        borderRight="1px solid"
        borderColor="smBorder"
      >
        <SidebarContent
          sections={sections}
          role={role}
          access={access}
          badges={badges}
          isCollapsed={isCollapsed}
          onToggle={toggle}
          onNavigate={onNavigate}
          onAction={onAction}
          pendingActionKind={pendingActionKind}
          regionId={NAV_REGION_ID}
          resolving={resolving}
        />
      </Box>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs" initialFocusRef={drawerNavRef}>
        {/* Chakra sizes the drawer's overlay, container and panel with 100vh, which iOS browsers miscompute. */}
        <DrawerOverlay {...ANCHORED_TO_VIEWPORT} />
        <DrawerContent h="auto" maxH="none" containerProps={ANCHORED_TO_VIEWPORT}>
          <DrawerBody p="0">
            <SidebarContent
              sections={sections}
              role={role}
              access={access}
              badges={badges}
              isCollapsed={isCollapsed}
              onToggle={toggle}
              onNavigate={onNavigate}
              onAction={onAction}
              pendingActionKind={pendingActionKind}
              navRef={drawerNavRef}
              resolving={resolving}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      {createEvent.isChoosingDepartment && (
        <ChooseEventDepartmentModal
          isOpen
          departments={createEvent.departments}
          isCreating={createEvent.isCreating}
          onCancel={createEvent.cancelChoosingDepartment}
          onConfirm={createEvent.confirmDepartment}
        />
      )}
      {showCreateContractModal && <CreateContractModal onClose={() => setShowCreateContractModal(false)} />}
      {showCreateProjectModal && <CreateProjectModal isOpen setShowModal={setShowCreateProjectModal} />}
    </>
  );
};
